import { Flex, Box, Icon, Tooltip } from '@chakra-ui/react';
import { useField } from 'formik';
import { useRef, useState, useMemo, useCallback } from 'react';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { FilterRowLabelField } from '../FilterRowLabelField';
import {
  CustomValueContainer,
  SelectV1,
} from '@/design-system/select/selectV1';
import { FormBuilder } from '../../../formBuilder';
import {
  FILTER_COMPARISON_CONDITIONS,
  FILTER_CONDITIONS,
  FILTER_CONDITIONS_WITH_AND,
} from '../../utils/constants';
import { useFilterCustomFields } from '../../hooks';
import { KText } from '@/ui-library/components';

// Types
interface FilterType {
  filter_by: {
    value: string;
    label: string;
  };
  filter_condition: any;
  filter_by_values: any[];
}

interface FilterRowProps {
  currentSelectedFilter: FilterType;
  idx: number;
  arrayHelpers: any;
  options: string[];
  customFilters: any[];
  expanded: boolean;
  previousSelectedFilter: any[];
  appliedFilters: any[];
  toggleExpandCollapse: () => void;
  containerRef?: React.RefObject<any>;
  filterConfig: any;
  accordionTitle: string;
  showAccordion: boolean;
  showSearchInput: boolean;
}

export const FilterRow = ({
  arrayHelpers,
  idx = 0,
  options = [],
  customFilters,
  expanded,
  currentSelectedFilter,
  previousSelectedFilter,
  appliedFilters,
  toggleExpandCollapse,
  filterConfig,
  accordionTitle,
  showAccordion,
  showSearchInput,
}: FilterRowProps) => {
  // Hooks
  const [{ value }, , { setValue }] = useField(`filters[${idx}]`);
  const [valueFieldLength, setValueFieldLength] = useState(0);
  const formRef = useRef();

  // Process config with dynamic values and parent filter validation
  const processedConfig = useMemo(() => {
    const newConfig = { ...filterConfig };
    // Track which fields need to be removed due to missing parent values
    const fieldsToRemove = new Set();

    // Process each filter config
    Object.keys(newConfig).forEach((key) => {
      const config = newConfig[key];

      // Handle parent filter dependency if it exists
      if (config?.parentFilterId) {
        // Find the parent filter in previously selected filters
        const parentFilter = previousSelectedFilter?.find(
          (filter) => filter?.filter_by?.value === config.parentFilterId
        );

        // Get the first selected value from parent filter (if any)
        const parentSelectedValue = parentFilter?.filter_by_values?.[0];

        // New concept:
        // - If parent exists with one value -> use one config (e.g. asyncSelect)
        // - If parent doesn't exist or has multiple values -> use another config (e.g. select)
        const customValidation =
          config?.validateChild ||
          (() => ({
            isValid: true,
            config: { isDisabled: false },
          }));
        const validationResult = customValidation({
          parentFilter,
          value,
          parentSelectedValue,
        });

        // If validation returns additional config, merge it with the current config
        // while preserving original config properties

        newConfig[key] = {
          ...config, // Keep original config
          ...validationResult.config, // Override with validation config
        };

        // Only mark for removal if this is a legacy config that doesn't provide
        // alternative config for invalid state
        if (
          !validationResult.config &&
          !validationResult.isValid &&
          value?.filter_by?.value === key &&
          !config?.alwaysEnable
        ) {
          fieldsToRemove.add(key);
        }
      }
    });

    // Remove child filter rows that depend on removed/invalid parent values
    if (fieldsToRemove.size > 0 && value?.filter_by?.value) {
      // Use setTimeout to avoid state updates during render
      setTimeout(() => {
        arrayHelpers.remove(idx);
      }, 0);
    }

    return newConfig;
  }, [filterConfig, previousSelectedFilter, value, arrayHelpers, idx]);

  // Memoized values
  const filterByValue = value?.filter_by?.value;
  const customFilterId = value?.filter_by?.id; // Get custom field ID if available
  const customFilterConfig = useMemo(
    () =>
      customFilters?.find(
        (i) =>
          i?.id === customFilterId ||
          i?.id === filterByValue ||
          i?.label === filterByValue
      ),
    [customFilters, filterByValue, customFilterId]
  );

  // Get custom filter field config
  const customFilterField = useFilterCustomFields({
    customFilterConfig,
    expanded,
    // for custom field - use label if available, otherwise value
    name: customFilterConfig?.label || filterByValue,
    selectedFilter: appliedFilters,
    toggleExpandCollapse,
  });

  // Transform filter values to static options
  const staticOptions = useMemo(
    () =>
      value?.filter_by_values.map((item) => ({
        label: item.value || item.name || item.full_address,
        value: item,
      })),
    [value?.filter_by_values]
  );

  // open the current selected filter or fallback to the first option of the filter
  const currentRowValueConfig = filterByValue
    ? processedConfig[filterByValue]
    : processedConfig[options?.[0]];

  // Merge custom fields with current config
  const currentFieldConfig = customFilterConfig
    ? customFilterField
    : {
        // spread the current row value config
        ...currentRowValueConfig,
        // add common items
        defaultOptions: staticOptions,
        expanded,
        // disable the row if the filter is not selected
        ...(currentSelectedFilter?.filter_by?.value ? {} : { disabled: true }),
        components: {
          ValueContainer: (customProps) => (
            <CustomValueContainer
              {...customProps}
              expanded={expanded}
              selectedFilter={value?.filter_by_values || appliedFilters}
              toggleExpandCollapse={toggleExpandCollapse}
            />
          ),
          ...(currentRowValueConfig?.components || {}),
        },
      };

  // Helper function to get filter conditions
  // const getFilterConditionsByFilter = (filter) => {
  //   // Get the filter configuration for the current filter
  //   const currentFilterConfig = processedConfig?.[filter?.value];

  //   // Check if it's a custom filter
  //   const customFilterConfig = customFilters?.find(
  //     (i) => i.id === filter?.label
  //   );
  //   // Use custom filter config if available, otherwise use the filter config from props
  //   const config = customFilterConfig
  //     ? customFilterField
  //     : currentFilterConfig || {
  //         ...currentFieldConfig,
  //         expanded,
  //         components: {
  //           ValueContainer: (customProps) => (
  //             <CustomValueContainer
  //               {...customProps}
  //               expanded={expanded}
  //               selectedFilter={appliedFilters}
  //               toggleExpandCollapse={toggleExpandCollapse}
  //             />
  //           ),
  //           ...(currentFieldConfig?.components || {}),
  //         },
  //       };

  //   return config?.isCompare ? FILTER_COMPARISON_CONDITIONS : FILTER_CONDITIONS;
  // };

  // Create a utility function that handles both regular and custom filters
  const determineFilterConditions = (
    filterValue,
    processedConfig,
    customFilters,
    customFilterFieldGenerator
  ) => {
    // Check if it's a custom filter
    const customFilterConfig = customFilters?.find((i) => i.id === filterValue);

    if (customFilterConfig) {
      // For custom filters, we need to generate the field config
      const tempCustomField = customFilterFieldGenerator({
        customFilterConfig,
        expanded: true,
        name: filterValue,
        selectedFilter: [],
        toggleExpandCollapse: () => {},
      });

      return tempCustomField?.isCompare
        ? FILTER_COMPARISON_CONDITIONS
        : FILTER_CONDITIONS;
    } else {
      const filterConfig = processedConfig[filterValue];
      return filterConfig?.isCompare
        ? FILTER_COMPARISON_CONDITIONS
        : FILTER_CONDITIONS;
    }
  };

  // Then in your component:
  const handleSelectChange = (filter_by) => {
    const currentConfig = processedConfig[filter_by?.value];
    const isCustomConfig = currentConfig?.customConditions;

    const handleConditions = determineFilterConditions(
      filter_by?.value,
      processedConfig,
      customFilters,
      useFilterCustomFields // Pass the hook function itself
    );

    setValue({
      ...value,
      filter_by,
      filter_by_values: [],
      filter_condition: isCustomConfig
        ? currentConfig?.customConditions[0]
        : handleConditions[0],
    });
  };

  const handleFormBuilderChange = useCallback(
    (formValues) => {
      const [firstKey] = Object.keys(formValues) || [];

      const filterVal = formValues[firstKey];

      const extractedValues = Array.isArray(filterVal)
        ? filterVal
        : filterVal
          ? [filterVal]
          : [];

      setValueFieldLength(extractedValues.length);
      setValue({
        ...value,
        filter_by_values: extractedValues.length > 0 ? extractedValues : [],
        formConfig: { ...currentFieldConfig },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, setValue]
  );

  // Initial form values - use custom filter label if available, otherwise use the field value
  const formFieldName = customFilterConfig?.label || value?.filter_by?.value;
  const initialFormValues = {
    [formFieldName]: currentFieldConfig?.isMulti
      ? value?.filter_by_values
      : value?.filter_by_values[0],
  };

  const handleFilterConditionalOptions = (key) => {
    const currentConfig = processedConfig[key?.filter_by?.value];
    const isCustomConfig = currentConfig?.customConditions;

    if (isCustomConfig) {
      return currentConfig.customConditions;
    }

    const getDefaultConditions = () => {
      // enable "AND(Include All)" condition only for skills, tags, skill_ids, tag_ids
      const andConditionTags = [
        'skills',
        'tags',
        'skill_ids',
        'tag_ids',
        'office_ids',
      ];
      if (andConditionTags.includes(key?.filter_by?.value)) {
        return FILTER_CONDITIONS_WITH_AND;
      }
      return FILTER_CONDITIONS;
    };

    return key?.filter_by?.value
      ? currentFieldConfig?.isCompare
        ? FILTER_COMPARISON_CONDITIONS
        : getDefaultConditions()
      : getDefaultConditions();
  };

  // When the current row's filter is config-disabled (e.g. a rejected-only
  // filter viewed from a non-Rejected stage), grey the row, lock the inner
  // controls, and surface the reason on hover. Delete stays clickable so the
  // user can remove a stale filter.
  const isRowDisabled = !!(filterByValue && currentRowValueConfig?.isDisabled);
  // Prefer `inactiveRowTooltip` for the applied-row hover so we can show a
  // different copy from the picker-dropdown tooltip; fall back to the legacy
  // `disabledFilterInfoMessage` if not provided.
  const disabledRowTooltip =
    currentRowValueConfig?.inactiveRowTooltip ||
    currentRowValueConfig?.disabledFilterInfoMessage ||
    '';
  const lockedFieldsProps = isRowDisabled
    ? { pointerEvents: 'none' as const, opacity: 0.5 }
    : {};

  const rowContent = (
    <Flex
      justifyContent="space-between"
      gap="6px"
      role="group"
      mb="12px"
      cursor={isRowDisabled ? 'not-allowed' : undefined}
    >
      {/* Label */}
      <KText variant="heading_4" mt="8px" fontWeight={600} width="30px">
        {idx === 0 ? 'By' : 'And'}
      </KText>

      {/* Filter Fields */}
      <Box width="180px" {...lockedFieldsProps}>
        <FilterRowLabelField
          options={options}
          value={value?.filter_by}
          onChange={handleSelectChange}
          customFilters={customFilters}
          previousSelectedFilter={previousSelectedFilter}
          filterConfig={processedConfig}
          accordionTitle={accordionTitle}
          showAccordion={showAccordion}
          showSearchInput={showSearchInput}
        />
      </Box>

      {/* Condition Select */}
      {!currentFieldConfig?.isBoolean && (
        <Box width="180px" {...lockedFieldsProps}>
          <SelectV1
            options={handleFilterConditionalOptions(value)}
            value={value?.filter_condition}
            onChange={(filter_condition) => {
              setValue({ ...value, filter_condition });
            }}
            isDisabled={
              isRowDisabled || !currentSelectedFilter?.filter_by?.value
            }
          />
        </Box>
      )}

      {/* Form Builder */}
      <Box
        width={valueFieldLength > 2 ? '500px' : '302px'}
        flex="1"
        {...lockedFieldsProps}
      >
        <FormBuilder
          initialValues={initialFormValues}
          config={[currentFieldConfig]}
          formRef={formRef}
          isObserveEnabled={true}
          observFunc={handleFormBuilderChange}
        />
      </Box>

      {/* Delete Button */}
      <Box
        _hover={{
          bg: 'secondary-50',
          cursor: 'pointer',
        }}
        p="2"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="sm"
        onClick={() => arrayHelpers.remove(idx)}
      >
        <Icon as={Trash01} mt="4px" h="16px" w="16px" />
      </Box>
    </Flex>
  );

  if (isRowDisabled && disabledRowTooltip) {
    return (
      <Tooltip label={disabledRowTooltip} placement="top" hasArrow>
        {rowContent}
      </Tooltip>
    );
  }
  return rowContent;
};
